import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { roomsApi, aiApi, sandboxApi } from '../services/api';
import { Award, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import AuthModal from '../components/AuthModal';
import Footer from '../components/Footer';
import { RoomHeader } from '../components/RoomHeader';
import { RoomTerminalCard } from '../components/RoomTerminalCard';
import { RoomInfoTabs } from '../components/RoomInfoTabs';
import { RoomTasksList } from '../components/RoomTasksList';
import UpgradePrompt from '../components/UpgradePrompt';

// Default placeholder tasks shown when a room has not seeded its own task list.
// Kept inline because the rest of the page treats them as a literal `tasks` array.
const DEFAULT_TASKS = [
  { id: 0, title: 'Connect to the machine', description: 'Start the virtual machine and establish connection' },
  { id: 1, title: 'Find the hidden flag', description: 'Use terminal commands to locate the flag in the system' },
  { id: 2, title: 'Analyze the vulnerability', description: 'Identify the security weakness in the application' },
  { id: 3, title: 'Exploit and capture', description: 'Successfully exploit the vulnerability and capture the final flag' },
];

const PROMPT = 'user@cyberinsti:~$';

const RoomDetail = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isPro, user, refreshProfile } = useAuth();

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roomStarted, setRoomStarted] = useState(false);
  const [progress, setProgress] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const [command, setCommand] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [taskHints, setTaskHints] = useState({});
  const [hintLoadingId, setHintLoadingId] = useState(null);
  const [hintQuota, setHintQuota] = useState(null);
  const [terminalOutput, setTerminalOutput] = useState([
    { type: 'system', text: 'Welcome to CyberInsti Terminal' },
    { type: 'system', text: 'Type "help" for available commands' },
    { type: 'system', text: 'Complete tasks to earn points and badges' },
    { type: 'prompt', text: PROMPT },
  ]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);

  // Per-task answer state — keyed by task.id. Free users (and Pro who want
  // to skip the terminal) submit answers directly inside each task row.
  const [taskFeedback, setTaskFeedback] = useState({}); // {[taskId]: {type, message}}
  const [taskSubmittingId, setTaskSubmittingId] = useState(null);

  // Sandbox credits — drives whether the live terminal renders for Free
  // users (balance > 0) or whether we show the soft out-of-credits panel.
  const [creditsState, setCreditsState] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(true);

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        setLoading(true);
        const roomData = await roomsApi.getById(roomId);
        setRoom(roomData);

        if (isAuthenticated) {
          try {
            const progressData = await roomsApi.getProgress(roomId);
            if (progressData && progressData.status !== 'not_started') {
              setRoomStarted(true);
              setProgress(progressData);
              setCompletedTasks(progressData.completedTasks || []);
            }
          } catch (err) {
            // No progress yet — fresh room.
            console.debug('[room] no progress yet:', err?.message);
          }
        }
      } catch (error) {
        console.error('Error fetching room:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRoom();
  }, [roomId, isAuthenticated]);

  // Pull AI-hint quota for the badge label. Pro users get `null` quota
  // (unlimited) — tracked separately by tier so the label can render
  // accurately on first paint.
  useEffect(() => {
    if (!isAuthenticated) {
      setHintQuota(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const q = await aiApi.getHintQuota();
        if (!cancelled) setHintQuota(q);
      } catch (err) {
        console.debug('[room] hint quota fetch failed:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Sandbox credits — determines whether Free users see the live xterm or
  // the soft out-of-credits panel.
  useEffect(() => {
    if (!isAuthenticated) {
      setCreditsState(null);
      setCreditsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCreditsLoading(true);
    (async () => {
      try {
        const s = await sandboxApi.getCredits();
        if (!cancelled) setCreditsState(s);
      } catch (err) {
        console.debug('[room] credits fetch failed:', err?.message);
      } finally {
        if (!cancelled) setCreditsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, isPro]);

  const handleStartRoom = async () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    try {
      await roomsApi.start(roomId);
      setRoomStarted(true);
      setTerminalOutput((prev) => [
        ...prev,
        { type: 'success', text: '🚀 Room started! Good luck!' },
        { type: 'prompt', text: PROMPT },
      ]);
    } catch (error) {
      console.error('Error starting room:', error);
      const msg = error.message || '';
      if (msg.toLowerCase().includes('free tier') || msg.toLowerCase().includes('limit')) {
        alert(`${msg}\n\nRedirecting to Pricing...`);
        navigate('/pricing');
      } else {
        alert(msg || 'Could not start room');
      }
    }
  };

  const handleSubmitFlag = async (flag, taskId = null) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return { success: false, message: 'Please login first' };
    }
    try {
      const result = await roomsApi.submitFlag(roomId, flag, taskId);
      if (result.success) {
        if (taskId !== null && !completedTasks.includes(taskId)) {
          setCompletedTasks([...completedTasks, taskId]);
        }
        await refreshProfile();
      }
      return result;
    } catch (error) {
      console.error('Error submitting flag:', error);
      return { success: false, message: error.message };
    }
  };

  const handleAnalyzeThreat = async (logs) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setAiLoading(true);
    try {
      const result = await aiApi.analyzeThreat(logs, `Room: ${room?.title}`);
      setAiAnalysis(result.analysis);
      setTerminalOutput((prev) => [
        ...prev,
        { type: 'system', text: '🤖 AI Analysis Complete:' },
        { type: 'output', text: `${result.analysis.substring(0, 500)}...` },
        { type: 'prompt', text: PROMPT },
      ]);
    } catch (error) {
      console.error('AI analysis error:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleTaskHint = async (taskId) => {
    setHintLoadingId(taskId);
    try {
      const result = await aiApi.roomHint(roomId, taskId, null);
      setTaskHints((prev) => ({ ...prev, [taskId]: result.hint }));
      if (result.quota) setHintQuota(result.quota);
    } catch (err) {
      // 402 = Free monthly hint limit reached. Surface a friendly inline upsell
      // instead of bouncing the user to /pricing — they keep room context.
      if (err.status === 402) {
        setTaskHints((prev) => ({
          ...prev,
          [taskId]: 'You have used all your free AI hints for this month. Upgrade to Pro for unlimited hints (see /pricing).',
        }));
      } else {
        setTaskHints((prev) => ({ ...prev, [taskId]: `Could not load hint: ${err.message}` }));
      }
    } finally {
      setHintLoadingId(null);
    }
  };

  const handleHint = async (taskIdArg = null) => {
    setAiLoading(true);
    try {
      const taskId =
        taskIdArg !== null
          ? parseInt(taskIdArg, 10)
          : (room?.tasks || []).find((t) => !completedTasks.includes(t.id))?.id ?? null;
      const result = await aiApi.roomHint(roomId, taskId, null);
      setTerminalOutput((prev) => [
        ...prev,
        { type: 'system', text: `AI hint${taskId !== null ? ` (task ${taskId})` : ''}:` },
        { type: 'output', text: result.hint },
        { type: 'prompt', text: PROMPT },
      ]);
    } catch (err) {
      setTerminalOutput((prev) => [
        ...prev,
        { type: 'error', text: `Hint failed: ${err.message}` },
        { type: 'prompt', text: PROMPT },
      ]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleTaskAnswerSubmit = async (taskId, value) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return false;
    }
    if (!roomStarted) {
      setTaskFeedback((prev) => ({
        ...prev,
        [taskId]: { type: 'error', message: 'Start the machine first.' },
      }));
      return false;
    }

    setTaskSubmittingId(taskId);
    setTaskFeedback((prev) => ({ ...prev, [taskId]: null }));
    const result = await handleSubmitFlag(value, taskId);
    setTaskSubmittingId(null);

    if (result.success) {
      setTaskFeedback((prev) => ({
        ...prev,
        [taskId]: {
          type: 'success',
          message: `${result.message} +${result.pointsEarned || 0} pts${
            result.roomCompleted ? ' — Room complete!' : ''
          }`,
        },
      }));
      return true;
    }
    setTaskFeedback((prev) => ({
      ...prev,
      [taskId]: { type: 'error', message: result.message || 'Incorrect — try again.' },
    }));
    return false;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length === 0) return;
      const next = historyIndex < 0 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(next);
      setCommand(commandHistory[next] || '');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex < 0) return;
      const next = historyIndex + 1;
      if (next >= commandHistory.length) {
        setHistoryIndex(-1);
        setCommand('');
      } else {
        setHistoryIndex(next);
        setCommand(commandHistory[next]);
      }
    }
  };

  const handleCommand = async (e) => {
    e.preventDefault();
    const raw = command.trim();
    if (!raw) return;

    setCommandHistory((prev) => [...prev, raw]);
    setHistoryIndex(-1);

    const newOutput = [...terminalOutput];
    newOutput.push({ type: 'input', text: `${PROMPT} ${raw}` });

    const lower = raw.toLowerCase();
    const [cmd, ...rest] = raw.split(/\s+/);
    const argStr = rest.join(' ');

    const pushPrompt = () => newOutput.push({ type: 'prompt', text: PROMPT });

    if (lower === 'help') {
      newOutput.push({
        type: 'output',
        text:
          'Available commands:\n' +
          '  help              Show this help\n' +
          '  clear             Clear terminal\n' +
          '  whoami            Current user\n' +
          '  pwd               Current directory\n' +
          '  id                User/group IDs\n' +
          '  ls [dir]          List files\n' +
          '  cat <file>        Read a file\n' +
          '  tasks             Show room tasks\n' +
          '  status            Show progress in this room\n' +
          '  ifconfig          Network interfaces\n' +
          '  netstat           Active connections\n' +
          '  nmap <target>     Port scan (simulated)\n' +
          '  ps                Running processes\n' +
          '  hint [task_id]    AI hint for a task (Pro)\n' +
          '  analyze <logs>    AI threat analysis (Pro)\n' +
          '  flag{...}         Submit a flag',
      });
    } else if (lower === 'clear') {
      setTerminalOutput([{ type: 'prompt', text: PROMPT }]);
      setCommand('');
      return;
    } else if (lower === 'whoami') {
      newOutput.push({ type: 'output', text: 'user' });
    } else if (lower === 'pwd') {
      newOutput.push({ type: 'output', text: '/home/user' });
    } else if (lower === 'id') {
      newOutput.push({ type: 'output', text: 'uid=1000(user) gid=1000(user) groups=1000(user),27(sudo)' });
    } else if (cmd.toLowerCase() === 'ls') {
      newOutput.push({ type: 'output', text: 'readme.md\nsecret.txt\ndata.db\nlogs/\n.bash_history' });
    } else if (cmd.toLowerCase() === 'cat') {
      const f = (argStr || '').toLowerCase();
      if (!f) {
        newOutput.push({ type: 'error', text: 'cat: missing file operand' });
      } else if (f.includes('secret')) {
        const flag = room?.flags?.[0] || 'flag{welcome_to_cyberinsti_2025}';
        newOutput.push({ type: 'output', text: flag });
      } else if (f.includes('readme')) {
        newOutput.push({
          type: 'output',
          text: `# ${room?.title || 'Room'}\n\n${room?.description || ''}\n\nTry 'tasks' to see objectives. Type 'hint' for an AI tip.`,
        });
      } else if (f.includes('bash_history')) {
        newOutput.push({ type: 'output', text: 'nmap -sV 10.10.10.5\nsudo -l\nls -la /etc/shadow' });
      } else {
        newOutput.push({ type: 'error', text: `cat: ${argStr}: No such file or directory` });
      }
    } else if (lower === 'tasks') {
      const taskList = room?.tasks || [];
      if (taskList.length === 0) {
        newOutput.push({ type: 'output', text: 'No individual tasks. Submit the room flag to complete.' });
      } else {
        const lines = taskList
          .map((t) => `[${completedTasks.includes(t.id) ? 'x' : ' '}] ${t.id}. ${t.title} (${t.points} pts)`)
          .join('\n');
        newOutput.push({ type: 'output', text: lines });
      }
    } else if (lower === 'status') {
      const done = completedTasks.length;
      const total = (room?.tasks || []).length;
      const score = progress?.score ?? 0;
      newOutput.push({
        type: 'output',
        text: `Room: ${room?.title}\nStatus: ${roomStarted ? 'in_progress' : 'not_started'}\nTasks: ${done}/${total}\nScore: ${score}`,
      });
    } else if (lower === 'ifconfig') {
      newOutput.push({
        type: 'output',
        text:
          'eth0: flags=4163<UP,BROADCAST,RUNNING,MULTICAST>  mtu 1500\n' +
          '        inet 10.10.10.42  netmask 255.255.255.0\n' +
          '        ether de:ad:be:ef:00:01  txqueuelen 1000\n\n' +
          'lo: flags=73<UP,LOOPBACK,RUNNING>  mtu 65536\n' +
          '        inet 127.0.0.1  netmask 255.0.0.0',
      });
    } else if (lower === 'netstat') {
      newOutput.push({
        type: 'output',
        text:
          'Proto  Local Address        Foreign Address      State\n' +
          'tcp    0.0.0.0:22           *:*                  LISTEN\n' +
          'tcp    0.0.0.0:80           *:*                  LISTEN\n' +
          'tcp    10.10.10.42:50234    10.10.10.5:443       ESTABLISHED',
      });
    } else if (cmd.toLowerCase() === 'nmap') {
      const target = argStr || '10.10.10.5';
      newOutput.push({
        type: 'output',
        text:
          `Starting Nmap scan on ${target}...\n\n` +
          `PORT     STATE  SERVICE    VERSION\n` +
          `22/tcp   open   ssh        OpenSSH 8.2p1\n` +
          `80/tcp   open   http       nginx 1.18.0\n` +
          `443/tcp  open   https      nginx 1.18.0\n` +
          `3306/tcp closed mysql\n\n` +
          `Nmap done: 1 IP address scanned`,
      });
    } else if (lower === 'ps') {
      newOutput.push({
        type: 'output',
        text:
          'PID   USER   COMMAND\n' +
          '  1   root   /sbin/init\n' +
          '342   root   /usr/sbin/sshd -D\n' +
          '518   www    nginx: worker process\n' +
          '901   user   -bash\n' +
          '902   user   ps',
      });
    } else if (cmd.toLowerCase() === 'hint') {
      setTerminalOutput([...newOutput, { type: 'system', text: 'Generating AI hint...' }, { type: 'prompt', text: PROMPT }]);
      setCommand('');
      await handleHint(rest[0]);
      return;
    } else if (cmd.toLowerCase() === 'analyze') {
      if (!argStr) {
        newOutput.push({ type: 'error', text: 'analyze: supply log text. Example: analyze "Failed SSH x5 from 1.2.3.4"' });
      } else {
        setTerminalOutput([...newOutput, { type: 'system', text: 'Analyzing with AI...' }, { type: 'prompt', text: PROMPT }]);
        setCommand('');
        await handleAnalyzeThreat(argStr);
        return;
      }
    } else if (lower.startsWith('flag{')) {
      const taskId = (room?.tasks || []).find((t) => !completedTasks.includes(t.id))?.id ?? null;
      const result = await handleSubmitFlag(raw, taskId);
      if (result.success) {
        newOutput.push({
          type: 'success',
          text: `[+] ${result.message} +${result.pointsEarned || 0} points`,
        });
        if (result.roomCompleted) {
          newOutput.push({ type: 'success', text: '[*] Room completed!' });
        }
      } else {
        newOutput.push({ type: 'error', text: `[-] ${result.message}` });
      }
    } else {
      newOutput.push({
        type: 'error',
        text: `Command not found: ${cmd}. Type 'help' for available commands.`,
      });
    }

    pushPrompt();
    setTerminalOutput(newOutput);
    setCommand('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Room not found</h2>
          <Button onClick={() => navigate('/rooms')}>Back to Rooms</Button>
        </div>
      </div>
    );
  }

  const tasks = room.tasks || DEFAULT_TASKS;
  const completionPct = tasks.length > 0 ? Math.round((completedTasks.length / tasks.length) * 100) : 0;
  const isRoomCompleted =
    (tasks.length > 0 && completedTasks.length >= tasks.length) ||
    user?.completedRooms?.includes(roomId);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-16">
        <RoomHeader room={room} />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main column: terminal + room info tabs */}
          <div className="lg:col-span-2 space-y-6">
            <RoomTerminalCard
              room={room}
              roomId={roomId}
              isAuthenticated={isAuthenticated}
              isPro={isPro}
              roomStarted={roomStarted}
              terminalOutput={terminalOutput}
              command={command}
              onCommandChange={setCommand}
              onCommandSubmit={handleCommand}
              onKeyDown={handleKeyDown}
              onStartRoom={handleStartRoom}
              onAnalyzeThreat={handleAnalyzeThreat}
              aiLoading={aiLoading}
              creditsState={creditsState}
              creditsLoading={creditsLoading}
            />

            <RoomInfoTabs room={room} aiAnalysis={aiAnalysis} />
          </div>

          {/* Sidebar: tasks + progress */}
          <div className="space-y-6">
            <UpgradePrompt context="in_room" />
            <RoomTasksList
              tasks={tasks}
              completedTasks={completedTasks}
              taskHints={taskHints}
              hintLoadingId={hintLoadingId}
              isPro={isPro}
              hintQuota={hintQuota}
              onRequestHint={handleTaskHint}
              taskFeedback={taskFeedback}
              taskSubmittingId={taskSubmittingId}
              answerDisabled={!isAuthenticated || !roomStarted}
              onSubmitAnswer={handleTaskAnswerSubmit}
            />

            <Card className="bg-[#1A1A1A] border-gray-800" data-testid="room-progress-card">
              <CardHeader>
                <CardTitle className="text-white text-sm">Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#00FF00] mb-2">{completionPct}%</div>
                  <p className="text-sm text-gray-400">
                    {completedTasks.length} of {tasks.length} tasks completed
                  </p>
                </div>
                {isRoomCompleted && (
                  <Button
                    data-testid="view-certificate-btn"
                    onClick={() => navigate(`/certificate/${roomId}`)}
                    className="mt-4 w-full bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
                  >
                    <Award className="w-4 h-4 mr-2" />
                    View Certificate
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />

      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialTab="login"
        />
      )}
    </div>
  );
};

export default RoomDetail;
